import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { compressImage } from '../lib/image';
import { Save, Upload, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ColorInput = ({ label, value, onChange }) => (
  <div>
    <Label className="mb-2 block text-sm">{label}</Label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-16 border border-gray-200 rounded-lg cursor-pointer"
      />
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="h-11 flex-1" />
    </div>
  </div>
);

const ImageField = ({ label, value, onChange, hint }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = await compressImage(file, { maxW: 800, maxH: 800, quality: 0.88 });
      onChange(data);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="mb-2 block text-sm">{label}</Label>
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-contain" style={{ mixBlendMode: 'multiply' }} />
          ) : (
            <span className="text-xs text-gray-400">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-gray-200 hover:border-[#c4477a]/40 cursor-pointer text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
          </label>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          )}
          {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const AdminIdentity = () => {
  const { refresh } = useSiteConfig();
  const [data, setData] = useState({
    site_name: '',
    logo: null,
    favicon: null,
    colors: { primary: '#c4477a', primary_dark: '#a83668', accent: '#b54e7d', text_dark: '#5a1d3b', bg_light: '#fdf5f7' },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/identity').then((r) => {
      setData((d) => ({ ...d, ...r.data, colors: { ...d.colors, ...(r.data.colors || {}) } }));
    }).finally(() => setLoading(false));
  }, []);

  const setColor = (k, v) => setData((d) => ({ ...d, colors: { ...d.colors, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/identity', data);
      toast.success('Saved!');
      refresh();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Site Identity</h1>
        <p className="text-gray-500 text-sm">Logo, name and brand colors.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <div>
          <Label className="mb-2 block text-sm">Site Name</Label>
          <Input value={data.site_name || ''} onChange={(e) => setData({ ...data, site_name: e.target.value })} className="h-11" />
        </div>

        <ImageField
          label="Logo"
          value={data.logo}
          onChange={(v) => setData({ ...data, logo: v })}
          hint="Square image recommended (PNG/JPG, ideally with white/transparent background)"
        />

        <ImageField
          label="Favicon"
          value={data.favicon}
          onChange={(v) => setData({ ...data, favicon: v })}
          hint="Small icon shown in browser tab"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="font-display text-lg text-[#5a1d3b] mb-4" style={{ fontWeight: 500 }}>Brand Colors</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <ColorInput label="Primary" value={data.colors.primary} onChange={(v) => setColor('primary', v)} />
          <ColorInput label="Primary Dark (hover)" value={data.colors.primary_dark} onChange={(v) => setColor('primary_dark', v)} />
          <ColorInput label="Accent" value={data.colors.accent} onChange={(v) => setColor('accent', v)} />
          <ColorInput label="Text Dark" value={data.colors.text_dark} onChange={(v) => setColor('text_dark', v)} />
          <ColorInput label="Background Light" value={data.colors.bg_light} onChange={(v) => setColor('bg_light', v)} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-12 px-8 gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminIdentity;
